import { shipmentReference } from "@/lib/shipment-reference";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Search } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type Task, TaskStatus, TaskType } from "@/api.types.v2";
import { useTrips } from "@/api/trips";
import { useTasks } from "@/api/tasks";
import { getTaskTypeDisplay } from "./tasks.helper";
import { getOperatorDisplayName } from "@/lib/safe-access";
import { useUpdateTaskStatus } from "@/api/tasks";
import { useAssignTasksToTrip } from "@/api/tasks";
import { useUnassignTasksFromTrip } from "@/api/tasks";
import { CreateTaskDialog } from "./create-task-dialog";
import { Input } from "@/components/ui/input";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

// Task status badge colors
const getTaskStatusBadgeClass = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.PENDING:
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-300";
    case TaskStatus.IN_PROGRESS:
      return "bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-300";
    case TaskStatus.COMPLETED:
      return "bg-green-100 text-green-800 hover:bg-green-200 border-green-300";
    case TaskStatus.FAILED:
      return "bg-red-100 text-red-800 hover:bg-red-200 border-red-300";
    case TaskStatus.CANCELLED:
      return "bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300";
    default:
      return "bg-neutral-100 text-neutral-800 hover:bg-neutral-200 border-neutral-300";
  }
};

// Task type badge colors with better visual distinction
const getTaskTypeBadgeClass = (type: TaskType): string => {
  switch (type) {
    case TaskType.CUSTOMER_PICKUP:
      return "bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-300";
    case TaskType.CUSTOMER_DELIVERY:
      return "bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-300";
    case TaskType.CUSTOMER_RETURN:
      return "bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-300";
    case TaskType.CARRIER_DROPOFF:
      return "bg-green-100 text-green-800 hover:bg-green-200 border-green-300";
    case TaskType.CARRIER_PICKUP:
      return "bg-teal-100 text-teal-800 hover:bg-teal-200 border-teal-300";
    case TaskType.DEPOT_RECEIVE:
      return "bg-slate-100 text-slate-800 hover:bg-slate-200 border-slate-300";
    case TaskType.DEPOT_SORT:
      return "bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300";
    case TaskType.DEPOT_DISPATCH:
      return "bg-zinc-100 text-zinc-800 hover:bg-zinc-200 border-zinc-300";
    case TaskType.DEPOT_TRANSFER:
      return "bg-stone-100 text-stone-800 hover:bg-stone-200 border-stone-300";
    default:
      return "bg-neutral-100 text-neutral-800 hover:bg-neutral-200 border-neutral-300";
  }
};

interface SimpleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function SimpleDialog({ isOpen, onClose, children }: SimpleDialogProps) {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        {children}
      </div>
    </>
  );
}

export function TasksTable() {
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);

  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [tripFilter, setTripFilter] = useState<string | null>(null);

  // Use the paginated tasks API
  const {
    data: tasksResponse,
    isLoading,
    refetch,
  } = useTasks({
    page,
    limit,
    status: statusFilter,
    type: typeFilter,
    search: searchTerm || undefined,
    tripId: tripFilter,
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  });

  // Extract tasks and metadata
  const tasks = tasksResponse?.data ?? [];
  const metadata = tasksResponse?.metadata;

  // Trip selector in the assign-to-trip dialog is also carrier-scoped so the
  // dropdown only shows trips the user could legitimately assign tasks to.
  const { data: availableTrips } = useTrips({
    carrierIds: carrierIds.join(","),
  });

  const updateTaskStatus = useUpdateTaskStatus({
    onSuccess: () => {
      toast.success("Task status updated successfully");
      void refetch();
    },
    onError: (error) => {
      console.error("Error updating task status:", error);
      toast.error("Failed to update task status");
    },
  });

  const assignTaskToTrip = useAssignTasksToTrip({
    onSuccess: () => {
      toast.success("Task assigned to trip successfully");
      setIsAssignDialogOpen(false);
      setSelectedTaskId(null);
      setSelectedTripId(null);
      void refetch();
    },
    onError: (error) => {
      console.error("Error assigning task to trip:", error);
      toast.error("Failed to assign task to trip");
    },
  });

  const unassignTaskFromTrip = useUnassignTasksFromTrip({
    onSuccess: () => {
      toast.success("Task unassigned from trip successfully");
      void refetch();
    },
    onError: (error) => {
      console.error("Error unassigning task from trip:", error);
      toast.error("Failed to unassign task from trip");
    },
  });

  const handleUpdateStatus = (taskId: string, newStatus: Task["status"]) => {
    updateTaskStatus.mutate({ taskId, status: newStatus });
  };

  const handleAssignToTrip = (taskId: string, tripId: string) => {
    // Remove focus from any active element before proceeding
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    assignTaskToTrip.mutate({ tripId, taskIds: [taskId] });
  };

  const handleUnassignFromTrip = (taskId: string) => {
    const task = tasks?.find((t) => t.id === taskId);
    if (!task?.trip?.id) {
      toast.error("Task is not assigned to a trip");
      return;
    }
    unassignTaskFromTrip.mutate({ tripId: task.trip.id, taskIds: [taskId] });
  };

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
    void refetch();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <SimpleDialog
        isOpen={isAssignDialogOpen}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setSelectedTaskId(null);
          setSelectedTripId(null);
        }}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Select Trip</h2>
            <p className="text-sm text-gray-500">
              Choose a trip to assign this task to.
            </p>
          </div>

          <Select
            onValueChange={(tripId) => setSelectedTripId(tripId)}
            value={selectedTripId ?? undefined}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a trip" />
            </SelectTrigger>
            <SelectContent>
              {availableTrips?.data.map((trip) => (
                <SelectItem key={trip.id} value={trip.id}>
                  {`Trip #${trip.tripNumber}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAssignDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (selectedTaskId && selectedTripId) {
                  void handleAssignToTrip(selectedTaskId, selectedTripId);
                }
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </SimpleDialog>

      <div className="flex justify-between mb-4 flex-wrap gap-4">
        <div>
          <CreateTaskDialog onCreated={() => refetch()} />
        </div>
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex">
            <Input
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-r-none w-[200px]"
            />
            <Button type="submit" className="rounded-l-none">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          {/* Status Filter */}
          <Select
            value={statusFilter ?? "all"}
            onValueChange={(value) => {
              setStatusFilter(value === "all" ? undefined : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select
            value={typeFilter ?? "all"}
            onValueChange={(value) => {
              setTypeFilter(value === "all" ? undefined : value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="PICKUP">Pickup</SelectItem>
              <SelectItem value="DELIVERY">Delivery</SelectItem>
              <SelectItem value="DEPOT_RECEIVE">
                Dispatch Center Receive
              </SelectItem>
              <SelectItem value="DEPOT_SORT">Dispatch Center Sort</SelectItem>
              <SelectItem value="DEPOT_DISPATCH">
                Dispatch Center Dispatch
              </SelectItem>
              <SelectItem value="RETURN">Return</SelectItem>
            </SelectContent>
          </Select>

          {/* Trip Filter */}
          <Select
            value={
              tripFilter === null
                ? "all"
                : tripFilter === "null"
                  ? "unassigned"
                  : "assigned"
            }
            onValueChange={(value) => {
              setTripFilter(
                value === "all"
                  ? null
                  : value === "unassigned"
                    ? "null"
                    : "assigned"
              );
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Trip Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>

          {/* Results per page selector */}
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(parseInt(value, 10));
              setPage(1); // Reset to first page when changing the limit
            }}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Per page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-white z-10 min-w-[150px]">
                Task Details
              </TableHead>
              <TableHead className="min-w-[120px]">Type</TableHead>
              <TableHead className="min-w-[120px]">Status</TableHead>
              <TableHead className="min-w-[200px]">Contact</TableHead>
              <TableHead className="min-w-[150px]">Shipment</TableHead>
              <TableHead className="min-w-[200px]">Trip</TableHead>
              <TableHead className="min-w-[200px]">Operator</TableHead>
              <TableHead className="min-w-[150px]">Dependencies</TableHead>
              <TableHead className="min-w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length > 0 ? (
              tasks.map((task: Task) => (
                <TableRow key={task.id}>
                  <TableCell className="sticky left-0 bg-white">
                    <div>
                      <p className="font-medium">
                        {shipmentReference(
                          task.shipment?.shipmentNumber ??
                            task.shipmentSnapshot?.shipmentNumber
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        TSK-{task.taskNumber}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(task.createdAt).toLocaleDateString()}
                      </p>
                      {task.description && (
                        <p className="text-sm text-gray-500">
                          {task.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={getTaskTypeBadgeClass(task.type)}
                      variant="outline"
                    >
                      {getTaskTypeDisplay(task.type)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={getTaskStatusBadgeClass(task.status)}
                      variant="outline"
                    >
                      {task.status.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {task.contactName ||
                    task.contactPhone ||
                    task.contactEmail ? (
                      <div>
                        {task.contactName && (
                          <p className="font-medium">{task.contactName}</p>
                        )}
                        {task.contactPhone && (
                          <p className="text-sm text-gray-500">
                            {task.contactPhone}
                          </p>
                        )}
                        {task.contactEmail && (
                          <p className="text-sm text-gray-500">
                            {task.contactEmail}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No contact info</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">
                      {(() => {
                        const num =
                          (task.shipment as { shipmentNumber?: number } | null)
                            ?.shipmentNumber ??
                          (
                            task.shipmentSnapshot as {
                              shipmentNumber?: number;
                            } | null
                          )?.shipmentNumber;
                        return shipmentReference(num);
                      })()}
                    </p>
                  </TableCell>
                  <TableCell>
                    {task.trip ? (
                      <div>
                        <p className="font-medium">
                          {`Trip #${task.trip.tripNumber}`}
                        </p>
                        {task.trip.vehicle && (
                          <p className="text-sm text-gray-500">
                            Vehicle: {task.trip.vehicle.vehicleNumber}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Unassigned</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {task.operator ? (
                      <p className="font-medium">
                        {getOperatorDisplayName(task.operator)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">Unassigned</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      {task.parentTask && (
                        <p className="text-sm text-gray-500">
                          Parent: TSK-{task.parentTask.taskNumber}
                        </p>
                      )}
                      {task.childTasks && task.childTasks.length > 0 && (
                        <p className="text-sm text-gray-500">
                          Children: {task.childTasks.length}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {task.status === TaskStatus.IN_PROGRESS && (
                          <DropdownMenuItem
                            onClick={() =>
                              handleUpdateStatus(task.id, TaskStatus.COMPLETED)
                            }
                          >
                            Complete Task
                          </DropdownMenuItem>
                        )}
                        {!task.trip && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedTaskId(task.id);
                              setIsAssignDialogOpen(true);
                            }}
                          >
                            Assign to Trip
                          </DropdownMenuItem>
                        )}
                        {task.trip && (
                          <DropdownMenuItem
                            onClick={() => handleUnassignFromTrip(task.id)}
                          >
                            Unassign from Trip
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-6">
                  No tasks found matching the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {metadata && (
        <div className="flex items-center justify-between px-2">
          <div className="text-sm text-gray-500">
            Showing {metadata.total ? (page - 1) * limit + 1 : 0} to{" "}
            {Math.min(page * limit, metadata.total)} of {metadata.total} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= metadata.totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
